import React, { forwardRef } from 'react';
import { Placement } from '@philips-internal/oswald-ui-constants';
import { AbortableDismissEvent, ClickEvent, DismissEvent } from '@philips-internal/oswald-ui-core/lib/component/types/index.js';
import { MessageService, ModelService } from '@philips-internal/oswald-ui-core/lib/services/index.js';
import { classNames, createAbort, isEnumValid, isFunction, resolveBackgroundColor, resolveHSLAColor, resolveHSLColor, } from '@philips-internal/oswald-ui-utils';
import { ReactComponent as CloseIcon } from '../../../resources/assets/components/close-md.svg';
import { hideSensitiveData } from '../../utils/hide-sensitive-data.js';
import { InlineImage } from '../inline-image/inline-image.js';
import { TokenShape, TokenSize } from '../token-list/token-list-constants.js';
import { TooltipTrigger } from '../tooltip/tooltip-constants.js';
import { Tooltip } from '../tooltip/tooltip.js';
import { TokenProperties } from './token-types.js';
import './token.css';
function TokenDismissAction(props) {
    if (!props.dismissible) {
        return null;
    }
    return (React.createElement("div", { className: "c-token__close", onClick: props.onClick },
        React.createElement(CloseIcon, { className: "c-token__close-icon" })));
}
function TokenIcon(props) {
    if (!props.icon) {
        return null;
    }
    return (React.createElement("div", { className: "c-token__icon" },
        React.createElement(InlineImage, { src: props.icon })));
}
function TokenContent(props) {
    const { sensitiveData = TokenProperties.sensitiveData.defaultValue, } = props;
    const className = classNames({
        'c-token__text': true,
        'u-text-strikethrough': Boolean(props.strikethrough),
    });
    const { value } = ModelService.formatProps(props, ['value'], props.formatters);
    const text = hideSensitiveData(MessageService.translate(value), sensitiveData);
    return (React.createElement(Tooltip, { placement: Placement.BOTTOM, triggers: [TooltipTrigger.ELLIPSIS], value: text },
        React.createElement("span", { className: className }, text)));
}
function getTokenValue(props) {
    const ommitedKeys = ['onBeforeDismiss', 'onClick', 'onContextMenuClick', 'onDismiss', 'onDoubleClick'];
    const filteredEntries = Object.entries(props).filter(([key]) => !ommitedKeys.includes(key));
    return Object.fromEntries(filteredEntries);
}
function getTokenStyle(color, labelColor) {
    const resolvedHSLA85Color = resolveHSLAColor(color, .85);
    const resolvedLabelColor = resolveHSLColor(labelColor);
    const resolvedBackgroundColor = resolveBackgroundColor(color);
    return {
        '--tws-token-background-color': resolvedBackgroundColor.backgroundColor ?? 'hsla(var(--color-gray-75), 0.1)',
        '--tws-token-background-color-active': resolvedHSLA85Color ?? 'hsla(var(--color-gray-75), 0.15)',
        '--tws-token-background-color-focus': resolvedHSLA85Color ?? 'hsl(var(--color-gray-45))',
        '--tws-token-background-color-hover': resolvedHSLA85Color ?? 'hsla(var(--color-gray-75), 0.15)',
        '--tws-token-icon-color': resolvedLabelColor ?? resolvedBackgroundColor.color ?? 'hsl(var(--color-gray-75))',
        '--tws-token-text-color': resolvedLabelColor ?? resolvedBackgroundColor.color ?? 'hsl(var(--color-gray-75))',
        '--tws-token-text-color-focus': resolvedLabelColor ?? resolvedBackgroundColor.color ?? 'hsl(var(--color-gray-75))',
    };
}
function InternalToken(props, ref) {
    const { enabled = TokenProperties.enabled.defaultValue, } = props;
    const onDismissClick = () => {
        const abort = createAbort();
        if (isFunction(props.onBeforeDismiss)) {
            props.onBeforeDismiss(new AbortableDismissEvent({ abort, value: getTokenValue(props) }));
        }
        void abort.promise.then(aborted => {
            if (aborted) {
                return;
            }
            if (isFunction(props.onDismiss)) {
                props.onDismiss(new DismissEvent({ value: getTokenValue(props) }));
            }
        });
    };
    const onClickEvent = (callback) => {
        if (callback) {
            callback(new ClickEvent({ value: getTokenValue(props) }));
        }
    };
    const className = classNames({
        'c-token': true,
        'is-disabled': !enabled && !props.color,
        'is-dismissible': Boolean(props.dismissible),
        [`c-token--${props.shape}`]: isEnumValid(TokenShape, props.shape),
        [`c-token--${props.size}`]: isEnumValid(TokenSize, props.size),
        [`${props.className}`]: Boolean(props.className),
    });
    return (React.createElement("div", { className: className, onClick: () => onClickEvent(props.onClick), onContextMenu: () => onClickEvent(props.onContextMenuClick), onDoubleClick: () => onClickEvent(props.onDoubleClick), ref: ref, style: getTokenStyle(props.color, props.labelColor), "x-contrast": "bd" },
        React.createElement("div", { className: "c-token__content" },
            React.createElement(TokenIcon, { icon: props.icon }),
            React.createElement(TokenContent, { formatters: props.formatters, sensitiveData: props.sensitiveData, strikethrough: props.strikethrough, value: props.value }),
            React.createElement(TokenDismissAction, { dismissible: props.dismissible, onClick: onDismissClick }))));
}
const Token = forwardRef(InternalToken);
export { Token, };
//# sourceMappingURL=token.js.map